// Helpery pro praci s ISO tydny (ISO 8601) v ramci OperativyListPage.
// Vsechny operace se odehravaji v lokalnim case prohlizece — operativy jsou
// kalendarni datum (DateOnly na backendu, ne UTC moment), takze ladime
// s tim, jak uzivatel vnima "dnes" / "tento tyden".
//
// ISO 8601 pravidla:
//   - tyden zacina pondelim, dow v ISO je 1..7 (Mon=1, Sun=7)
//   - tyden 1 je ten, ktery obsahuje 4. leden (== tyden s prvnim ctvrtkem v roce)
//   - "iso week year" se muze lisit od kalendarniho roku: napr. 30.12.2024 patri
//     do ISO tydne 1 roku 2025

/** Vraci pondeli ISO tydne pro libovolne datum. Cas se nastavi na pulnoc. */
export function startOfIsoWeek(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // JavaScript getDay(): Sun=0..Sat=6. ISO chceme Mon=0..Sun=6 -> posun o -1 a modulo 7.
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  return d;
}

/**
 * Vraci ISO week-year a ISO week number (1..53).
 * Algoritmus: vezmi ctvrtek tehoz tydne, podle nej urci rok i tyden.
 */
export function isoWeekNumber(date: Date): { year: number; week: number } {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // Posun na ctvrtek (ISO: kazdy tyden ma prave jeden ctvrtek; rok tehoz ctvrtka je ISO rok).
  // getDay(): Sun=0..Sat=6. ISO chceme Mon=1..Sun=7 -> ((dow+6)%7)+1.
  const dayIso = ((d.getDay() + 6) % 7) + 1;
  d.setDate(d.getDate() + (4 - dayIso));
  const year = d.getFullYear();
  // 1. leden ISO roku
  const yearStart = new Date(year, 0, 1);
  const diffMs = d.getTime() - yearStart.getTime();
  const week = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000)) + 1;
  return { year, week };
}

/** "YYYY-MM-DD" z Date v lokalnim case (ne UTC). */
export function isoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Cesky nazev dne pro index 0..4 (jen Po-Pa, vikendy se neukazuji). */
export const DAY_LABELS_CS: readonly string[] = [
  'Pondělí',
  'Úterý',
  'Středa',
  'Čtvrtek',
  'Pátek',
];

/**
 * Vraci sformatovany rozsah pro tyden zacinajici v `monday`.
 * Priklady:
 *   25.5.2026 -> "25. 5. – 29. 5. 2026"   (pondeli az patek, stejny mesic a rok)
 *   30.3.2026 -> "30. 3. – 3. 4. 2026"    (pres prelom mesice)
 *   28.12.2026 -> "28. 12. 2026 – 1. 1. 2027" (pres prelom roku)
 */
export function formatWeekRangeCs(monday: Date): string {
  const friday = new Date(monday);
  friday.setDate(friday.getDate() + 4);

  const md = monday.getDate();
  const mm = monday.getMonth() + 1;
  const my = monday.getFullYear();
  const fd = friday.getDate();
  const fm = friday.getMonth() + 1;
  const fy = friday.getFullYear();

  if (my === fy && mm === fm) {
    // stejny mesic i rok
    return `${md}. ${mm}. – ${fd}. ${fm}. ${fy}`;
  }
  if (my === fy) {
    // stejny rok, jiny mesic
    return `${md}. ${mm}. – ${fd}. ${fm}. ${fy}`;
  }
  // jiny rok
  return `${md}. ${mm}. ${my} – ${fd}. ${fm}. ${fy}`;
}

/** Posun "dnes" o `days` dni a vrati lokalni YYYY-MM-DD. */
export function isoDateOffset(days: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return isoDate(d);
}

/**
 * Z datumu (Date nebo "YYYY-MM-DD") vrati novou instanci. Pro string parsovanou
 * jako lokalni datum (ne UTC, jak by udelal new Date("2026-05-25") sam o sobe).
 */
export function parseIsoDate(s: string): Date {
  const [y, m, d] = s.split('-').map((x) => parseInt(x, 10));
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}
